import type { HierarchyNode } from "../entities/access";

/** Узел дерева: сотрудник, его прямые подчинённые и размер поддерева. */
export interface TreeNode {
  node: HierarchyNode;
  children: TreeNode[];
  /** Все нижестоящие, включая косвенных. */
  descendants: number;
}

export interface Hierarchy {
  /** Корни с подчинёнными: каждый из них — вершина отдельной структуры. */
  roots: TreeNode[];
  /** Без руководителя и без подчинённых: в структуру ещё не встроены. */
  unplaced: HierarchyNode[];
  byId: Map<string, TreeNode>;
  /** Кто чей непосредственный руководитель в собранном дереве. */
  parentOf: Map<string, string>;
  /** Число уровней в самой глубокой цепочке. */
  depth: number;
}

/**
 * Собирает дерево из плоского списка.
 *
 * Сервер не допускает циклов, но обход всё равно защищён от них: иначе
 * испорченные данные зависили бы интерфейс вместо того, чтобы показать
 * сотрудников отдельными корнями.
 */
export function buildHierarchy(nodes: readonly HierarchyNode[]): Hierarchy {
  const known = new Set(nodes.map((node) => node.id));
  const childrenOf = new Map<string, HierarchyNode[]>();
  const topLevel: HierarchyNode[] = [];
  for (const node of nodes) {
    if (node.manager_id !== null && known.has(node.manager_id)) {
      const siblings = childrenOf.get(node.manager_id) ?? [];
      siblings.push(node);
      childrenOf.set(node.manager_id, siblings);
    } else {
      topLevel.push(node);
    }
  }

  const byId = new Map<string, TreeNode>();
  const parentOf = new Map<string, string>();
  let depth = 0;
  const build = (node: HierarchyNode, level: number): TreeNode => {
    depth = Math.max(depth, level);
    const tree: TreeNode = { node, children: [], descendants: 0 };
    byId.set(node.id, tree);
    for (const child of childrenOf.get(node.id) ?? []) {
      if (byId.has(child.id)) continue;
      parentOf.set(child.id, node.id);
      const built = build(child, level + 1);
      tree.children.push(built);
      tree.descendants += 1 + built.descendants;
    }
    return tree;
  };

  const trees = topLevel.map((node) => build(node, 1));
  // Узлы цикла не достижимы ни из одного корня — показываем их отдельно.
  for (const node of nodes) {
    if (!byId.has(node.id)) trees.push(build(node, 1));
  }

  return {
    roots: trees.filter((tree) => tree.children.length > 0),
    unplaced: trees
      .filter((tree) => tree.children.length === 0)
      .map((tree) => tree.node),
    byId,
    parentOf,
    depth,
  };
}

/** Сам сотрудник и все нижестоящие: под них его нельзя переставлять. */
export function selfAndDescendantIds(tree: TreeNode): Set<string> {
  const ids = new Set<string>();
  const visit = (current: TreeNode) => {
    ids.add(current.node.id);
    current.children.forEach(visit);
  };
  visit(tree);
  return ids;
}

/** Руководители сотрудника вверх по цепочке, ближайший первым. */
export function ancestorIds(hierarchy: Hierarchy, id: string): string[] {
  const chain: string[] = [];
  let cursor = hierarchy.parentOf.get(id);
  while (cursor !== undefined && !chain.includes(cursor)) {
    chain.push(cursor);
    cursor = hierarchy.parentOf.get(cursor);
  }
  return chain;
}

function matches(node: HierarchyNode, query: string): boolean {
  return `${node.display_name} ${node.position ?? ""} ${node.email}`
    .toLocaleLowerCase("ru-RU")
    .includes(query);
}

/**
 * Что показывать при поиске: совпавшие сотрудники и цепочка их руководителей,
 * чтобы найденный человек не повисал в воздухе. Пустой запрос — без фильтра.
 */
export function searchVisibility(
  hierarchy: Hierarchy,
  rawQuery: string,
): { matched: Set<string>; visible: Set<string> } | null {
  const query = rawQuery.trim().toLocaleLowerCase("ru-RU");
  if (query === "") return null;

  const matched = new Set<string>();
  for (const tree of hierarchy.byId.values()) {
    if (matches(tree.node, query)) matched.add(tree.node.id);
  }
  const visible = new Set(matched);
  for (const id of matched) {
    for (const ancestor of ancestorIds(hierarchy, id)) visible.add(ancestor);
  }
  return { matched, visible };
}

/** Идентификаторы всех, у кого есть подчинённые: их можно свернуть. */
export function branchIds(hierarchy: Hierarchy): string[] {
  return [...hierarchy.byId.values()]
    .filter((tree) => tree.children.length > 0)
    .map((tree) => tree.node.id);
}
